import React, { useState, useEffect, useRef, useCallback } from 'react';
import './App.css';
import Header from './components/Header';
import SubCategoryGrid from './components/SubCategoryGrid';
import SearchBar from './components/SearchBar';
import Filters from './components/Filters';
import SoundGrid from './components/SoundGrid';
import FavoritesPage from './components/FavoritesPage';
import Footer from './components/Footer';
import AboutPage from './components/AboutPage';
import MentionsLegales from './components/MentionsLegales';
import PolitiqueConfidentialite from './components/PolitiqueConfidentialite';
import Credits from './components/Credits';
import DiscoverHeader from './components/DiscoverHeader';
import CookieConsent from 'react-cookie-consent';
import { loadAdsense } from './utils/loadAdsense';
import { useLocation, useNavigate } from 'react-router-dom';
import { 
  FaSun, FaMoon, FaTree, FaWater, FaMountain, FaCloudRain, FaWind, 
  FaLeaf, FaDove, FaPaw, FaBolt, FaCity, FaCoffee, FaSubway, FaPlane, FaRoad, FaBriefcase, FaUsers, 
  FaSpa, FaUmbrellaBeach, FaGhost, FaBomb, FaMousePointer, FaRocket, FaMagic, FaCube, 
  FaHandRock, FaSmileBeam, FaRandom, FaExclamationTriangle, FaCogs, FaSpaceShuttle, FaBell, FaSquare, FaFire, FaBus, FaBeer, FaBullhorn, FaStore, FaShoePrints
} from 'react-icons/fa';
import { useFavoritesContext } from './context/FavoritesContext';
import SEO from './components/SEO';
import { categoryToSlug, subcategoryToSlug, slugToCategory, slugToSubcategory, getCategoryUrl, getCanonicalUrl } from './utils/urlUtils';
import HomepageSEO from './components/HomepageSEO';
import { useSounds } from './hooks/useSounds';
import PerformanceOptimizer, { useWebVitals } from './components/PerformanceOptimizer';

function App() {
  // Hook pour mesurer les Core Web Vitals
  useWebVitals();
  
  const location = useLocation();
  const navigate = useNavigate();
  const params = new URLSearchParams(location.search);
  
  // Gérer la page depuis les paramètres URL
  const pageParam = params.get('page');
  const [currentPage, setCurrentPage] = useState(pageParam || 'home');
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedCategory, setSelectedCategory] = useState(params.get('cat') || 'all');
  const [selectedSubCategory, setSelectedSubCategory] = useState(params.get('subcat') || null);
  
  // Synchroniser l'état avec l'URL « propres » (et gérer la rétro-compatibilité)
  useEffect(() => {
    const { pathname, search } = location;
    const segments = pathname.split('/').filter(Boolean);

    if (segments.length === 0) {
      // Page d'accueil
      setCurrentPage('home');
      setSelectedCategory('all');
      setSelectedSubCategory(null);
    } else if (segments[0] === 'category') {
      // /category/:catSlug(/:subSlug)
      const cat = slugToCategory[segments[1]] || 'all';
      const sub = segments[2] ? slugToSubcategory[segments[2]] : null;
      setCurrentPage('home');
      setSelectedCategory(cat);
      setSelectedSubCategory(sub);
    } else {
      // Pages statiques (/favorites, /about, ...)
      setCurrentPage(segments[0]);
      setSelectedCategory('all');
      setSelectedSubCategory(null);
    }

    // ↩️ Gestion des anciennes URLs avec ?cat=&subcat=
    const params = new URLSearchParams(search);
    const catParam = params.get('cat');
    if (catParam) {
      const subParam = params.get('subcat');
      const clean = getCategoryUrl(catParam, subParam);
      navigate(clean, { replace: true });
    }
  }, [location.pathname, location.search]);
  
  // Fonction pour changer de page avec mise à jour de l'URL
  const handlePageChange = (page) => {
    let path = '/';
    if (page && page !== 'home') {
      path = `/${page}`;
    }
    navigate(path);
    setCurrentPage(page || 'home');
  }

  
  const handleCategorySelect = (cat) => {
    // Si on clique sur la catégorie déjà active
    if (cat === selectedCategory) {
      if (selectedSubCategory) {
        // 1️⃣ Cas : un sous-genre est actif → on revient à la vue « thème » (catégorie seule)
        navigate(getCategoryUrl(cat));
        setSelectedSubCategory(null);
        return;
      } else if (cat !== 'all') {
        // 2️⃣ Cas : on est déjà sur la vue « thème » → on revient à « Découvrir »
        navigate('/');
        setSelectedCategory('all');
        return;
      }
      // Si cat === 'all' et déjà sur all, ne rien faire.
    }

    // 3️⃣ Sélection d'une nouvelle catégorie ou retour à "all"
    if (cat === 'all') {
      navigate('/');
      setSelectedCategory('all');
      setSelectedSubCategory(null);
      return;
    }
    const url = getCategoryUrl(cat);
    navigate(url);
    setSelectedCategory(cat);
    setSelectedSubCategory(null);
    // Réinitialiser le terme de recherche quand on change de catégorie (hors "all")
    if (cat !== 'all') {
      setSearchTerm('');
    }
  };

  // Gestion de la sélection de sous-catégorie
  const handleSubCategorySelect = (sub) => {
    if (!selectedCategory || selectedCategory === 'all') return;
    const url = getCategoryUrl(selectedCategory, sub || null);
    navigate(url);
    setSelectedSubCategory(sub);
    setSearchTerm('');
  }


  const [selectedLicense, setSelectedLicense] = useState('all');
  const [selectedDuration, setSelectedDuration] = useState('all');
  const [sortBy, setSortBy] = useState('popular');
  const [quickFilters, setQuickFilters] = useState([]);
  const [darkMode, setDarkMode] = useState(() => {
    const savedTheme = localStorage.getItem('soundnest-theme');
    // Si un thème est sauvegardé, on l'utilise. Sinon, le mode sombre est le défaut.
    return savedTheme ? savedTheme === 'dark' : true;
  });
  
  const { sounds, loading, error } = useSounds();
  const { favorites, addToFavorites, removeFromFavorites, isFavorite } = useFavoritesContext();
  const [currentlyPlaying, setCurrentlyPlaying] = useState({ id: null, url: null });
  const audioRef = useRef(null);






  const handlePlay = (sound) => {
    setCurrentlyPlaying(prev => {
      if (prev.id === sound.id) {
        // If clicking the same sound, toggle pause
        if (audioRef.current) audioRef.current.pause();
        return { id: null, url: null };
      } else {
        // If clicking a new sound, play it
        return { id: sound.id, url: sound.downloadUrl };
      }
    });
  };

  // Arrêter le son quand on change de page ou de catégorie
  useEffect(() => {
    if (audioRef.current) {
      audioRef.current.pause();
    }
    setCurrentlyPlaying({ id: null, url: null });
  }, [currentPage, selectedCategory, selectedSubCategory]);

  useEffect(() => {
    const audioElement = audioRef.current;
    if (!audioElement) return;

    const handleEnded = () => setCurrentlyPlaying({ id: null, url: null });
    audioElement.addEventListener('ended', handleEnded);

    if (currentlyPlaying.url) {
      audioElement.src = currentlyPlaying.url;
      audioElement.play().catch(e => console.error("Playback failed", e));
    } else {
      audioElement.pause();
    }

    return () => {
      audioElement.removeEventListener('ended', handleEnded);
    };
  }, [currentlyPlaying]);



  // Charger AdSense en mode non personnalisé par défaut (consentement refusé)
  useEffect(() => {
    loadAdsense(false);
  }, []);

  // Appliquer le thème
  useEffect(() => {
    document.body.className = darkMode ? 'dark-theme' : 'light-theme';
    localStorage.setItem('soundnest-theme', darkMode ? 'dark' : 'light');
  }, [darkMode]);

  // Appliquer les filtres rapides
  const applyQuickFilters = (soundsList) => {
    if (quickFilters.length === 0) return soundsList;
    
    return soundsList.filter(sound => {
      return quickFilters.every(filter => {
        switch (filter) {
          case 'nature':
            return sound.tags.some(tag => 
              ['nature', 'oiseau', 'eau', 'vent', 'forêt', 'pluie'].includes(tag.toLowerCase())
            );
          case 'urban':
            return sound.tags.some(tag => 
              ['ville', 'trafic', 'metro', 'urbain', 'voiture'].includes(tag.toLowerCase())
            );
          case 'music':
            return sound.tags.some(tag => 
              ['musique', 'instrument', 'mélodie', 'rythme'].includes(tag.toLowerCase())
            );
          case 'voice':
            return sound.tags.some(tag => 
              ['voix', 'parole', 'chant', 'vocal'].includes(tag.toLowerCase())
            );
          case 'short':
            return parseDuration(sound.duration) < 10;
          case 'medium':
            const dur = parseDuration(sound.duration);
            return dur >= 10 && dur <= 60;
          default:
            return true;
        }
      });
    });
  };

  // Fonction pour parser la durée
  const parseDuration = (duration) => {
    const parts = duration.split(':');
    return parseInt(parts[0]) * 60 + parseInt(parts[1]);
  };

  // Trier les sons
  const applySorting = (soundsList) => {
    const sorted = [...soundsList];
    switch (sortBy) {
      case 'recent':
        return sorted.reverse(); // Inverse l'ordre par défaut
      case 'duration-asc':
        return sorted.sort((a, b) => parseDuration(a.duration) - parseDuration(b.duration));
      case 'duration-desc':
        return sorted.sort((a, b) => parseDuration(b.duration) - parseDuration(a.duration));
      case 'popular':
      default:
        return sorted; // Ordre par défaut
    }
  };

  // Helper pour comparaison sans accent (normalize + lower)
  const normalizeText = (str) => str ? str.normalize('NFD').replace(/[\u0300-\u036f]/g, '').toLowerCase() : '';

  // Filtrer les sons selon les critères sélectionnés
  let filteredSounds = sounds.filter(sound => {
    const normSubCat = normalizeText(selectedSubCategory);
    const normTheme   = normalizeText(sound.theme);

    // Enhanced subcategory matching with synonyms and relevance
    const subCategoryKeywords = {
      'feu': ['feu', 'incendie', 'flamme', 'bruler'],
      'eau': ['eau', 'riviere', 'goutte', 'pluie', 'vague'],
      'vent': ['vent', 'souffle', 'brise', 'rafale'],
      'oiseau': ['oiseau', 'chant', 'piaillement'],
      // Add more keywords for other subcategories as needed
    };

    const keywords = subCategoryKeywords[normSubCat] || [normSubCat];
    const matchesSubCategory = !selectedSubCategory || 
      keywords.some(keyword => 
        normTheme.includes(keyword) ||
        normalizeText(sound.title).includes(keyword) ||
        sound.tags.some(tag => normalizeText(tag).includes(keyword))
      );
    const normSearchTerm = normalizeText(searchTerm);
    const matchesSearch = normalizeText(sound.title).includes(normSearchTerm) ||
                         sound.tags.some(tag => normalizeText(tag).includes(normSearchTerm)) ||
                         normalizeText(sound.author).includes(normSearchTerm);
    
    // Map themes to categories for filtering - more comprehensive mapping
    const getThemeCategory = (theme) => {
      const key = normalizeText(theme);
      const themeToCategory = {
        // Effets sonores
        'swoosh': 'Effets sonores',
        'explosion': 'Effets sonores', 
        'impact': 'Effets sonores',
        'transition': 'Effets sonores',
        'cartoon': 'Effets sonores',
        'scifi': 'Effets sonores',
        'glitch': 'Effets sonores',
        'magique': 'Effets sonores',
        'metal': 'Effets sonores',
        'objet': 'Effets sonores',
        
        // Interface
        'interface': 'Interface',
        
        // Nature
        'foret': 'Nature',
        'ocean': 'Nature', 
        'pluie': 'Nature',
        'oiseaux': 'Nature',
        'montagne': 'Nature',
        'riviere': 'Nature',
        'desert': 'Nature',
        'vent': 'Nature',
        'feuillage': 'Nature',
        'animaux': 'Nature',
        'orage': 'Nature',
        'grotte': 'Nature',
        
        // Ambiance
        'ville': 'Ambiance',
        'cafe': 'Ambiance',
        'metro': 'Ambiance',
        'aeroport': 'Ambiance',
        'rue': 'Ambiance',
        'bureau': 'Ambiance',
        'foule': 'Ambiance',
        'manifestation': 'Ambiance',
        'plage': 'Ambiance',
        'nuit': 'Ambiance',
        'espace': 'Ambiance',
        'halloween': 'Ambiance'
      };
      
      return themeToCategory[key] || 'Effets sonores'; // Par défaut
    };
    
    const soundCategory = getThemeCategory(sound.theme);
    const matchesCategory = selectedCategory === 'all' || soundCategory === selectedCategory;
    const matchesLicense = selectedLicense === 'all' || sound.license === selectedLicense;
    
    let matchesDuration = true;
    if (selectedDuration !== 'all') {
      const duration = sound.duration;
      const [minutes, seconds] = duration.split(':').map(Number);
      const totalSeconds = minutes * 60 + seconds;
      
      switch (selectedDuration) {
        case 'short':
          matchesDuration = totalSeconds <= 60;
          break;
        case 'medium':
          matchesDuration = totalSeconds > 60 && totalSeconds <= 300;
          break;
        case 'long':
          matchesDuration = totalSeconds > 300;
          break;
        default:
          matchesDuration = true;
      }
    }
    
    return matchesSearch && matchesCategory && matchesLicense && matchesDuration && matchesSubCategory;
  });

  // Appliquer le tri principal
  filteredSounds = applySorting(filteredSounds);

  // Prioritize sounds with a more granular relevance score
  if (selectedSubCategory) {
    const normSubCat = normalizeText(selectedSubCategory);
    
    const getRelevanceScore = (sound, term) => {
      const normTitle = normalizeText(sound.title);
      const normTheme = normalizeText(sound.theme);

      // P1: Title starts with the term
      if (normTitle.startsWith(term)) return 0;
      // P2: Title contains the term as a whole word
      if (new RegExp(`\\b${term}\\b`).test(normTitle)) return 1;
      // P3: Theme contains the term
      if (normTheme.includes(term)) return 2;
      // P4: Title contains the term as a substring
      if (normTitle.includes(term)) return 3;
      // P5: Tags contain the term
      if (sound.tags.some(tag => normalizeText(tag).includes(term))) return 4;

      return 5; // Lowest priority
    };

    filteredSounds.sort((a, b) => {
      const scoreA = getRelevanceScore(a, normSubCat);
      const scoreB = getRelevanceScore(b, normSubCat);
      return scoreA - scoreB;
    });
  }

  const toggleDarkMode = () => {
    setDarkMode(!darkMode);
  };

  // ------------------ SEO Dynamic Metadata ------------------
  const baseDescription = 'Découvrez plus de 600 sons gratuits et libres de droits de qualité professionnelle. Téléchargez des effets sonores, ambiances et bruits pour enrichir vos projets créatifs, vidéos, podcasts et jeux. Formats MP3 et WAV disponibles.';

  let seoTitle = 'SoundNest';
  let seoDescription = baseDescription;

  if (currentPage === 'home') {
    if (searchTerm) {
      seoTitle = `Résultats pour "${searchTerm}" | SoundNest`;
      seoDescription = `Écoutez et téléchargez les meilleurs sons correspondant à "${searchTerm}". ${baseDescription}`;
    } else if (selectedCategory !== 'all') {
      if (selectedSubCategory) {
        seoTitle = `Sons ${selectedCategory} - ${selectedSubCategory} gratuits | SoundNest`;
        seoDescription = `Téléchargez des sons et effets sonores ${selectedCategory.toLowerCase()} (${selectedSubCategory}). ${baseDescription}`;
      } else {
        seoTitle = `Sons ${selectedCategory} gratuits | SoundNest`;
        seoDescription = `Téléchargez des sons et effets sonores de la catégorie ${selectedCategory}. ${baseDescription}`;
      }
    } else {
      seoTitle = 'Sons Gratuits et Libres de Droits | SoundNest';
    }
  } else if (currentPage === 'favorites') {
    seoTitle = 'Vos Favoris | SoundNest';
    seoDescription = 'Retrouvez tous les sons que vous avez ajoutés en favoris.';
  } else if (currentPage === 'about') {
    seoTitle = 'À propos | SoundNest';
    seoDescription = 'En savoir plus sur SoundNest et notre mission.';
  } else if (currentPage === 'mentions-legales') {
    seoTitle = 'Mentions Légales | SoundNest';
    seoDescription = 'Informations légales concernant le site SoundNest.';
  } else if (currentPage === 'politique-confidentialite') {
    seoTitle = 'Politique de Confidentialité | SoundNest';
    seoDescription = 'Découvrez comment SoundNest protège vos données personnelles.';
  } else if (currentPage === 'credits') {
    seoTitle = 'Crédits | SoundNest';
    seoDescription = 'Remerciements et crédits des ressources utilisées sur SoundNest.';
  }

  // Canonical URL and Breadcrumbs for category/subcategory pages
  const canonicalUrl = getCanonicalUrl(location.pathname);
  const seoBreadcrumbs = (currentPage === 'home' && selectedCategory !== 'all')
    ? [
        { name: selectedCategory, url: getCategoryUrl(selectedCategory) },
        ...(selectedSubCategory ? [{ name: selectedSubCategory, url: getCategoryUrl(selectedCategory, selectedSubCategory) }] : [])
      ]
    : [];

  if (loading) {
    return (
      <div className="app loading">
        <div className="loading-spinner">
          <div className="spinner"></div>
          <p>Chargement des sons...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="app error">
        <div className="error-message">
          <h2>Erreur de chargement</h2>
          <p>{error}</p>
          <button onClick={() => window.location.reload()}>Réessayer</button>
        </div>
      </div>
    );
  }

  return (
    <PerformanceOptimizer>
      <div className={`app ${darkMode ? 'dark' : 'light'}`}>
          {/* SEO metadata for non-sound pages (handled by SoundPage separately) */}
          <SEO
            title={seoTitle}
            description={seoDescription}
            currentUrl={canonicalUrl}
            breadcrumbs={seoBreadcrumbs}
          />
      <Header 
        currentPage={currentPage}
        setCurrentPage={handlePageChange}
        darkMode={darkMode}
        toggleDarkMode={toggleDarkMode}
        favoritesCount={favorites.length}
        selectedCategory={selectedCategory}
        setSelectedCategory={handleCategorySelect}
      />
      {/* Global audio element accessible depuis toutes les pages */}
      <audio ref={audioRef} />
      
      <div className="content-wrap">
      
      {currentPage === 'home' ? (
        <main className="main-content">
          {/* Rich SEO content component displayed on the main homepage */}
          {selectedCategory === 'all' && !searchTerm && !selectedSubCategory && (
            <HomepageSEO sounds={sounds} />
          )}
          <div className="search-section">
            {selectedCategory === 'all' && (
            <DiscoverHeader 
              soundsCount={filteredSounds.length}
              onSortChange={setSortBy}
              onFilterChange={setQuickFilters}
              selectedSort={sortBy}
              selectedFilters={quickFilters}
            />
          )}
          
          <SearchBar 
              searchTerm={searchTerm}
              setSearchTerm={setSearchTerm}
              sounds={sounds}
              selectedCategory={selectedCategory}
              setSelectedCategory={handleCategorySelect}
            />
          </div>

          {(() => {
              const subCatsMap = {
    'Effets sonores': [
      { id: 'swoosh', name: 'Swoosh', Icon: FaWind },
      { id: 'explosion', name: 'Explosion', Icon: FaBomb },
      { id: 'impact', name: 'Impact', Icon: FaHandRock },

      { id: 'cartoon', name: 'Cartoon', Icon: FaSmileBeam },
      { id: 'notification', name: 'Notification', Icon: FaBell },
      { id: 'marche', name: 'Marche', Icon: FaShoePrints },

      { id: 'objet', name: 'Objet', Icon: FaCube },
      { id: 'transition', name: 'Transition', Icon: FaRandom },
      { id: 'science fiction', name: 'Science Fiction', Icon: FaRocket },

      { id: 'magique', name: 'Magique', Icon: FaMagic },
      { id: 'metal', name: 'Métal', Icon: FaCogs },
      { id: 'bois', name: 'Bois', Icon: FaTree },
    ],
    'Nature': [
      { id: 'montagne', name: 'Montagne', Icon: FaMountain },
       { id: 'océan', name: 'Océan', Icon: FaWater },
       { id: 'feu', name: 'Feu', Icon: FaFire },
      { id: 'riviere', name: 'Rivière', Icon: FaWater },
       { id: 'desert', name: 'Désert', Icon: FaSun },

      { id: 'pluie', name: 'Pluie', Icon: FaCloudRain },
      { id: 'grotte', name: 'Grotte', Icon: FaMountain },
      { id: 'orage', name: 'Orage', Icon: FaBolt },
      { id: 'animaux', name: 'Animaux', Icon: FaPaw },
      { id: 'oiseaux', name: 'Oiseaux', Icon: FaDove },
      { id: 'feuillage', name: 'Feuillage', Icon: FaLeaf },
      { id: 'vent', name: 'Vent', Icon: FaWind },
    ],
    'Ambiance': [
      { id: 'ville', name: 'Ville', Icon: FaCity },
      { id: 'cafe', name: 'Café', Icon: FaCoffee },


      { id: 'rue', name: 'Rue', Icon: FaRoad },
       { id: 'bar', name: 'Bar', Icon: FaBeer },
       { id: 'bus', name: 'Bus', Icon: FaBus },
       { id: 'metro', name: 'Métro', Icon: FaSubway },

      { id: 'bureau', name: 'Bureau', Icon: FaBriefcase },
      { id: 'halloween', name: 'Halloween', Icon: FaGhost },
      { id: 'espace', name: 'Espace', Icon: FaSpaceShuttle },
      { id: 'nuit', name: 'Nuit', Icon: FaMoon },

       { id: 'manifestation', name: 'Manifestation', Icon: FaBullhorn },
      { id: 'foule', name: 'Foule', Icon: FaUsers },
    ],
  }; 
               
                  /*
                   { id: 'foret', name: 'Forêt', image: 'https://source.unsplash.com/featured/?forest' },
                  { id: 'ocean', name: 'Océan', image: 'https://source.unsplash.com/featured/?ocean' },
                  { id: 'montagne', name: 'Montagne', image: 'https://source.unsplash.com/featured/?mountain' },
                  { id: 'riviere', name: 'Rivière', image: 'https://source.unsplash.com/featured/?river' },
                  { id: 'desert', name: 'Désert', image: 'https://source.unsplash.com/featured/?desert' },
                  { id: 'pluie', name: 'Pluie', image: 'https://source.unsplash.com/featured/?rainforest' },
                  { id: 'vent', name: 'Vent', image: 'https://source.unsplash.com/featured/?wind' },
                  { id: 'feuillage', name: 'Feuillage', image: 'https://source.unsplash.com/featured/?leaves' },
                  { id: 'oiseaux', name: 'Oiseaux', image: 'https://source.unsplash.com/featured/?birds' },
                  { id: 'animaux', name: 'Animaux', image: 'https://source.unsplash.com/featured/?animals' },
                  { id: 'orage', name: 'Orage', image: 'https://source.unsplash.com/featured/?storm' },
                  { id: 'grotte', name: 'Grotte', image: 'https://source.unsplash.com/featured/?cave' }
                ],
                
                  { id: 'ville', name: 'Ville', image: 'https://source.unsplash.com/featured/?city' },
                  { id: 'cafe', name: 'Café', image: 'https://source.unsplash.com/featured/?coffee-shop' },
                  { id: 'metro', name: 'Métro', image: 'https://source.unsplash.com/featured/?subway' },
                  { id: 'aeroport', name: 'Aéroport', image: 'https://source.unsplash.com/featured/?airport' },
                  { id: 'rue', name: 'Rue', image: 'https://source.unsplash.com/featured/?street' },
                  { id: 'bureau', name: 'Bureau', image: 'https://source.unsplash.com/featured/?office' },
                  { id: 'foule', name: 'Foule', image: 'https://source.unsplash.com/featured/?crowd' },
                  { id: 'spa', name: 'Spa', image: 'https://source.unsplash.com/featured/?spa' },
                  { id: 'plage', name: 'Plage', image: 'https://source.unsplash.com/featured/?beach' },
                  { id: 'nuit', name: 'Nuit', image: 'https://source.unsplash.com/featured/?night' },
                  { id: 'espace', name: 'Espace', image: 'https://source.unsplash.com/featured/?space' },
                  { id: 'halloween', name: 'Halloween', image: 'https://source.unsplash.com/featured/?halloween' }
                ],
                'Effets sonores': [
                  { id: 'swooshes', name: 'Swooshes', image: 'https://source.unsplash.com/featured/?motion' },
                  { id: 'explosion', name: 'Explosion', image: 'https://source.unsplash.com/featured/?explosion' },
                  { id: 'impact', name: 'Impact', image: 'https://source.unsplash.com/featured/?impact' },
                  { id: 'interface', name: 'Interface Utilisateur', image: 'https://source.unsplash.com/featured/?ui' },
                  { id: 'cartoon', name: 'Cartoon', image: 'https://source.unsplash.com/featured/?cartoon' },
                  { id: 'objet', name: 'Objet', image: 'https://source.unsplash.com/featured/?object' },
                  { id: 'transition', name: 'Transition', image: 'https://source.unsplash.com/featured/?transition' },
                  { id: 'scifi', name: 'Sci-Fi', image: 'https://source.unsplash.com/featured/?scifi' },
                  { id: 'glitch', name: 'Glitch', image: 'https://source.unsplash.com/featured/?glitch' },
               */
               const subCats = subCatsMap[selectedCategory] || [];

              if (subCats.length > 0 && !selectedSubCategory && selectedCategory !== 'all') {
                return (
                  <SubCategoryGrid subCategories={subCats} onSelect={handleSubCategorySelect} />
                );
              }
              return (
                <>
                  {(() => {
                    // Affiche le nom et l'icône de la catégorie / sous-catégorie courante
                    if (selectedCategory === 'all') return null;
                    let displayName = '';
                    let IconComp = null;
                    const subCatsArr = subCatsMap[selectedCategory] || [];
                    if (selectedSubCategory) {
                      const sub = subCatsArr.find(sc => sc.id === selectedSubCategory);
                      displayName = sub ? sub.name : selectedSubCategory;
                      IconComp   = sub ? sub.Icon : null;
                    } else {
                      displayName = selectedCategory;
                    }
                    return (
                      <div className="category-header" style={{display:'flex',alignItems:'center',gap:'0.5rem',marginBottom:'1rem'}}>
                        {IconComp && <IconComp size={28} />}
                        <h2 style={{margin:0}}>{displayName}</h2>
                      </div>
                    );
                  })()}

                  <SoundGrid 
                    sounds={filteredSounds}
                    favorites={favorites}
                    isFavorite={isFavorite}
                    onToggleFavorite={(sound) => {
                      if (isFavorite(sound.id)) {
                        removeFromFavorites(sound.id);
                      } else {
                        addToFavorites(sound);
                      }
                    }}
                    currentlyPlayingId={currentlyPlaying.id}
                    onPlay={handlePlay}
                  />
                  {filteredSounds.length === 0 && (
                    <div className="no-results">
                      <h3>Aucun son trouvé</h3>
                      <p>Essayez de modifier vos critères de recherche ou vos filtres.</p>
                    </div>
                  )}
                </>
              );
            })()
          }
        </main>
      ) : currentPage === 'about' ? (
        <AboutPage setCurrentPage={handlePageChange} />
      ) : currentPage === 'mentions-legales' ? (
        <MentionsLegales setCurrentPage={handlePageChange} />
      ) : currentPage === 'politique-confidentialite' ? (
        <PolitiqueConfidentialite />
      ) : currentPage === 'credits' ? (
        <Credits />
      ) : (
        <FavoritesPage 
          favorites={favorites}
          removeFromFavorites={removeFromFavorites}
          isFavorite={isFavorite}
          currentlyPlayingId={currentlyPlaying.id}
          onPlay={handlePlay}
        />
      )}
      </div>

      <Footer currentPage={currentPage} setCurrentPage={handlePageChange} />

      <CookieConsent
        location="bottom"
        buttonText="Accepter"
        declineButtonText="Refuser"
        enableDeclineButton
        cookieName="soundnest-cookie-consent"
        style={{ background: "var(--card-bg)", color: "var(--text-primary)", borderTop: "1px solid var(--border-color)" }}
        buttonStyle={{ background: "var(--primary-color)", color: "#fff", borderRadius: "6px", padding: "0.5rem 1rem" }}
        declineButtonStyle={{ background: "var(--border-color)", color: "var(--text-primary)", borderRadius: "6px", padding: "0.5rem 1rem" }}
        expires={365}
        onAccept={() => {
          // Reload AdSense only if we hadn't loaded personalized ads yet
          loadAdsense(true);
          // Update Google Consent Mode if you use it
          if (window.gtag) {
            window.gtag('consent', 'update', {
              ad_storage: 'granted',
              analytics_storage: 'granted',
            });
          }
          
        }}
        onDecline={() => {
          loadAdsense(false);
          if (window.gtag) {
            window.gtag('consent', 'default', {
              ad_storage: 'denied',
              analytics_storage: 'denied',
            });
          }
        }}
      >
        Ce site utilise des cookies pour vous proposer des publicités personnalisées et améliorer votre expérience.
      </CookieConsent>
      </div>
    </PerformanceOptimizer>
  );
}

export default App;
